import { PathPlannerOperationMode } from "@ptx-autonomous/libs/api";
import type {
	PlannedPathCollection,
	SimplePoint,
} from "@ptx-autonomous/libs/api-models";
import type { LineString, MultiPolygon, Polygon, Position } from "geojson";
import type { PreMissionMetrics } from "./metrics";

export type PassOrder =
	| "SkipOrder"
	| "ConsecutiveOrder"
	| "MultiSkipOrder"
	| "CustomOrder";

export type HeadlandSizeSearch = "SearchOn" | "SearchOff";

export type GetPathOptions = {
	heading: number;
	overlap: number;
	swathExtension: number;
	prefersConsecutiveOrder: boolean;
	targetSpeed: number;
	turningSpeed: number;
	approachSpeed: number;
	startLocation?: Position;
	uncapHeadlands: boolean;
	done: () => void;
};

export type ReplanPathOptions = {
	uncapHeadlands: boolean;
	allowNonConsecutiveTurns: boolean;
	optimizeStartPoint: boolean;
};

export type PathPlannerOptions = {
	heading: number;
	depth: number;
	implementWidthMeters: number;
	swathExtensionLength: number;
	turningRadiusMeters: number;
	overlap: number;
	pattern: PassOrder;
	operationMode: PathPlannerOperationMode;
	skipPass?: number;
};

export const DubinsSteeringStateSpace = "CC00_Dubins" as const;
export const DualCurvatureSteeringStateSpace = "CC00_Dual_Curvature" as const;
export type SteeringStateSpace =
	| typeof DubinsSteeringStateSpace
	| typeof DualCurvatureSteeringStateSpace;

export type ReferenceABLine = {
	ReferencePoint: { Lat: number; Long: number; Alt: number };
	ABLineHeading: number;
};

export type PathPlanningRequest = {
	RowHeading: number;
	Depth: number;
	ImplementWidthMeters: number;
	SwathExtensionLength: number;
	TurningRadiusMeters: number;
	TractorTurningRadiusMeters: number;
	HitchToImplementAxle: number;
	RearAxleToHitch: number;
	OperationMode: PathPlannerOperationMode;
	TurnPointDistance: number;
	FieldBoundary: SimplePoint[];
	Overlap: number;
	Obstacles: SimplePoint[][];
	PassOrder: PassOrder;
	StartPoint?: SimplePoint;
	ReferenceABLine?: ReferenceABLine;
	HeadlandSearchIncrement: number;
	HeadlandSizeSearch?: HeadlandSizeSearch;
	SteeringStateSpace: SteeringStateSpace;
	boundaryID?: string;
	boundaryName?: string;
	requestID?: string;
	machineID?: string;
	machineName?: string;
	userID?: string;
	accountID?: string;
};

export type LoggingInfo = {
	boundaryID: string;
	boundaryName: string;
	requestID: string;
	machineID: string;
	machineName: string;
	userID: string;
	accountID: string;
};

export type ParametricPathSegment = {
	CurvatureRate: number;
	East: number;
	InitialCurvature: number;
	InitialHeading: number;
	Length: number;
	MaxSpeedKph: number;
	North: number;
	SegmentMetadata: string;
	SegmentType: "Swath" | "Turn";
	Up: number;
};

export type ParametricPathMetadata = {
	Created: string;
	Datum: string;
	ID: string;
};

export type ParametricPath = {
	Metadata: ParametricPathMetadata;
	Origin: SimplePoint;
	Segments: ParametricPathSegment[];
	Type: string;
};

export type PathPlanningResponse = {
	Boundary: {
		InnerFieldBoundary: SimplePoint[];
	};
	Boundaries: {
		InnerFieldBoundaries: SimplePoint[][];
	};
	MitigationData: {
		CorrectedTurns: SimplePoint[][];
		ShortenedSegments: {
			Start: SimplePoint;
			End: SimplePoint;
		}[];
	};
	Path: PlannedPathCollection[];
	ParametricPath: ParametricPath;
	Version: string;
	requestID: string;
};

export interface PathPlanningOptions {
	heading: number;
	overlap: number;
	swathExtension: number;
	prefersConsecutiveOrder: boolean;
	targetSpeed: number;
	turningSpeed: number;
	approachSpeed: number;
	startLocation?: Position;
	done: () => void;
}

export interface SimplePathPlanningOptions {
	heading: number;
	tillagePattern: PassOrder;
	operationMode: PathPlannerOperationMode;
	overlap: number;
	swathExtensionLength: number;
	depth: number;
}

export type PlanPathResult = {
	passOrder: PassOrder;
	startOption: "user" | "optimal";
	skipPass?: number;
	pathResult?: {
		path: LineString;
		estimatedCoverage?: Polygon | MultiPolygon;
		validDepth: number;
		pathPoints: SimplePoint[];
		preMissionMetrics: PreMissionMetrics;
		clothoid: ParametricPath;
		mitigationData?: {
			correctedTurns: SimplePoint[][];
			shortenedSegments: {
				Start: SimplePoint;
				End: SimplePoint;
			}[];
		};
	};
	pathLengthOptimal: boolean;
	workAreaAreaOptimal: boolean;
	totalTimeOptimal: boolean;
	headlandDepthOptimal: boolean;
	turnTimeOptimal?: boolean;
	efficiencyOptimal: boolean;
	requestId: string;
	version: string;
};

export type PlanPathResults = Array<PlanPathResult>;

export type PathSortingOption = "efficiency" | "area" | "time";

export const HEADLANDS_MAX_DEPTH = 2;
export const HEADLANDS_MIN_DEPTH = 1;
export const UNCAP_HEADLANDS_MAX_DEPTH = 8;
export const UNCAP_HEADLANDS_MIN_DEPTH = 2;
export const PASS_ORDER_VARIANTS = 6;
export const RESUME_THRESHOLD_DISTANCE_M = 5;
